import {
  isValid,
  parseISO,
  parse,
  format,
  formatDistance,
  formatDistanceToNowStrict,
  add,
  sub,
  isAfter,
  isBefore,
  compareAsc,
  getTime,
  hoursToSeconds,
  hoursToMinutes,
  minutesToSeconds,
  minutesToHours,
  setDayOfYear,
  setDate,
  addDays,
  FormatDistanceOptions,
  min,
  isSameMonth,
  isSameYear,
  isSameDay,
  differenceInHours as differenceInHoursFn,
  Duration,
  intervalToDuration,
  isSameWeek,
  eachDayOfInterval,
  getDay,
} from 'date-fns';

import { CalendarUnit, DateOrISOString, TimeUnit } from './types';

import {
  DIFFERENCE_IN_CALENDAR_TIME_MAP,
  DIFFERENCE_IN_MAP,
  END_OF_MAP,
  GET_TIME_MAP,
  SET_TIME_MAP,
  START_OF_MAP,
} from './mapping';
import { DATE_FORMAT } from 'constant/date';

// const SPECIAL_VALID_FORMAT = ['PPpp', 'PPPppp', 'PPPPpppp'];

// const replaceToken = (token: string): string => {
//   if (SPECIAL_VALID_FORMAT.includes(token)) {
//     return token;
//   }

//   if (token.length <= 1) {
//     return DATE_FORMATS_MAP[token];
//   }

//   const firstChar = token[0];
//   const secondChar = token[1];

//   if (firstChar !== secondChar && token.length === 2) {
//     return DATE_FORMATS_MAP[token];
//   }

//   let newToken = '';
//   for (const char of token) {
//     newToken += DATE_FORMATS_MAP[char];
//   }

//   return newToken;
// };

// const isValidFormatToken = (token: string): boolean => {
//   // Special cases for 'PPpp', 'PPPppp', 'PPPPpppp'
//   if (SPECIAL_VALID_FORMAT.includes(token)) {
//     return true;
//   }

//   // Handle single-character tokens (e.g., 'a', 'd', 'H', etc.)
//   if (token.length <= 1) {
//     return token in DATE_FORMATS_MAP;
//   }

//   const firstChar = token[0];
//   const secondChar = token[1];

//   // Handle tokens where the first two characters are different
//   // e.g., 'do', 'Mo', 'Qo', 'ho', 'Ko', etc.
//   if (firstChar !== secondChar && token.length === 2) {
//     return token in DATE_FORMATS_MAP;
//   }

//   // Handle tokens where all characters are the same
//   // e.g., 'd', 'dd', 'ddd', 'dddd', etc.
//   if (firstChar === secondChar) {
//     for (const char of token) {
//       // Check if each character is valid and matches the first character
//       if (!(char in DATE_FORMATS_MAP) || char !== firstChar) {
//         return false;
//       }
//     }
//     return true;
//   }

//   // For any other cases, return false
//   return false;
// };

// nosonar
// const validateAndReplaceFormatString = (format: string): string => {
//   // delimeters: [space,/,comma,.,:,-]
//   const delimiters = /[\s/,.:-]+/;
//   const tokens = format.split(delimiters);
//   let validFormat = format;
//   for (const token of tokens) {
//     const isValidToken = isValidFormatToken(token);
//     if (!isValidToken) {
//       throw new Error(`Unsupported format: ${token}`);
//     }
//     const newToken = replaceToken(token);
//     validFormat = validFormat.replace(token, newToken);
//   }
//   return validFormat;
// };

export const parseWithFormat = (
  dateString: string,
  formatString: string, // date-fns format string
  referenceDate: DateOrISOString = new Date(),
): Date => {
  // This validation and replacement logic is necessary only if we decide to
  // replace `date-fns` with another date library in the future
  // and update the mapping values in `mapping.ts` accordingly.
  // Using it works as well, but it's not necessary for `date-fns`.
  // const validFormatString = validateAndReplaceFormatString(formatString);
  // const parsedDate = parse(
  //   dateString,
  //   validFormatString,
  //   parseDate(referenceDate),
  // );

  const parsedDate = parse(dateString, formatString, parseDate(referenceDate));

  return parsedDate;
};

export const formatDate = (
  date: DateOrISOString,
  formatString: string, // date-fns format string
  // separator: string = '/',
): string => {
  const parsedDate = parseDate(date);

  // This validation and replacement logic is necessary only if we decide to
  // replace `date-fns` with another date library in the future
  // and update the mapping values in `mapping.ts` accordingly.
  // Using it works as well, but it's not necessary for `date-fns`.
  // const validFormatString = validateAndReplaceFormatString(formatString);
  // const baseFormat = validFormatString.replace(/\//g, separator);

  return format(parsedDate, formatString);
};

export const formatDateDistance = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
  options?: FormatDistanceOptions,
): string => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return formatDistance(parsedDateLeft, parsedDateRight, options);
};

export const formatDateDistanceToNowStrict = (
  date: DateOrISOString,
): string => {
  const parsedDate = parseDate(date);
  return formatDistanceToNowStrict(parsedDate);
};

export const isValidDate = (date: DateOrISOString): boolean => {
  const parsedDate = parseDate(date);
  return isValid(parsedDate);
};

export const parseDate = (date: DateOrISOString): Date => {
  if (typeof date === 'string') {
    const parsedDate = parseISO(date);
    return parsedDate;
  }
  return date;
};

export const addTime = (
  date: DateOrISOString,
  amount: number,
  unit: TimeUnit = TimeUnit.DAYS,
): Date => {
  const parsedDate = parseDate(date);
  return add(parsedDate, { [unit]: amount });
};

export const subTime = (
  date: DateOrISOString,
  amount: number,
  unit: TimeUnit = TimeUnit.DAYS,
): Date => {
  const parsedDate = parseDate(date);
  return sub(parsedDate, { [unit]: amount });
};

export const earliestDate = (dates: DateOrISOString[]): Date => {
  const parsedDates = dates.map(parseDate);
  return min(parsedDates);
};

export const differenceInHours = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): number => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);

  // If the dates are on the same day and the left time is earlier than the right time,
  // we need to add 24 hours to get the correct difference
  if (
    isSameDay(parsedDateLeft, parsedDateRight) &&
    isBefore(parsedDateLeft, parsedDateRight)
  ) {
    return differenceInHoursFn(parsedDateLeft, parsedDateRight) + 24;
  }

  return differenceInHoursFn(parsedDateLeft, parsedDateRight);
};

export const differenceInTime = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
  unit: TimeUnit = TimeUnit.DAYS,
): number => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);

  const differenceInFn = DIFFERENCE_IN_MAP[unit];

  if (!differenceInFn) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  return differenceInFn(parsedDateLeft, parsedDateRight);
};

export const differenceInCalendarTime = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
  unit: CalendarUnit = CalendarUnit.DAYS,
): number => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);

  const differenceInCalendarFn = DIFFERENCE_IN_CALENDAR_TIME_MAP[unit];

  if (!differenceInCalendarFn) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  return differenceInCalendarFn(parsedDateLeft, parsedDateRight);
};

export const startOfTime = (
  date: DateOrISOString,
  unit: TimeUnit = TimeUnit.DAYS,
): Date => {
  const parsedDate = parseDate(date);
  const startOfFn = START_OF_MAP[unit];

  if (!startOfFn) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  return startOfFn(parsedDate);
};

export const endOfTime = (
  date: DateOrISOString,
  unit: TimeUnit = TimeUnit.DAYS,
): Date => {
  const parsedDate = parseDate(date);
  const endOfFn = END_OF_MAP[unit];

  if (!endOfFn) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  return endOfFn(parsedDate);
};

export const getTimeUnit = (
  date: DateOrISOString,
  unit: TimeUnit = TimeUnit.DAYS,
): number => {
  const parsedDate = parseDate(date);
  const getTimeFn = GET_TIME_MAP[unit];

  if (!getTimeFn) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  return getTimeFn(parsedDate);
};

export const setTimeUnit = (
  date: DateOrISOString,
  unit: TimeUnit = TimeUnit.DAYS,
  value: number,
): Date => {
  const parsedDate = parseDate(date);
  const setTimeFn = SET_TIME_MAP[unit];

  if (!setTimeFn) {
    throw new Error(`Unsupported unit: ${unit}`);
  }

  return setTimeFn(parsedDate, value);
};

export const getTimeStamp = (date: DateOrISOString): number => {
  const parsedDate = parseDate(date);
  return getTime(parsedDate);
};

export const isDateAfter = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): boolean => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return isAfter(parsedDateLeft, parsedDateRight);
};

export const isDateBefore = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): boolean => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return isBefore(parsedDateLeft, parsedDateRight);
};

export const compareDateAsc = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): number => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return compareAsc(parsedDateLeft, parsedDateRight);
};

export const hoursToTime = (
  hours: number,
  unit: Extract<TimeUnit, 'seconds' | 'minutes'>,
): number => {
  if (unit === TimeUnit.SECONDS) return hoursToSeconds(hours);
  if (unit === TimeUnit.MINUTES) return hoursToMinutes(hours);

  throw new Error(`Unsupported unit: ${unit}`);
};

export const minutesToTime = (
  minutes: number,
  unit: Extract<TimeUnit, 'seconds' | 'hours'>,
): number => {
  if (unit === TimeUnit.SECONDS) return minutesToSeconds(minutes);
  if (unit === TimeUnit.HOURS) return minutesToHours(minutes);

  throw new Error(`Unsupported unit: ${unit}`);
};

export const setDayOfTime = (
  date: DateOrISOString,
  day: number,
  unit: Extract<TimeUnit, 'months' | 'years'>,
): Date => {
  const parsedDate = parseDate(date);
  if (unit === TimeUnit.MONTHS) return setDate(parsedDate, day);
  if (unit === TimeUnit.YEARS) return setDayOfYear(parsedDate, day);

  throw new Error(`Unsupported unit: ${unit}`);
};

export const addDaysToDate = (date: DateOrISOString, days: number): Date => {
  const parsedDate = parseDate(date);
  return addDays(parsedDate, days);
};

// Are the given dates on the same day?
export const isDateOfSameDay = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): boolean => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return isSameDay(parsedDateLeft, parsedDateRight);
};

// Are the given dates in the same week (and year)?
export const isDateOfSameWeek = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): boolean => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return isSameWeek(parsedDateLeft, parsedDateRight);
};

// Are the given dates in the same month (and year)?
export const isDateOfSameMonth = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): boolean => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return isSameMonth(parsedDateLeft, parsedDateRight);
};

// Are the given dates in the same year?
export const isDateOfSameYear = (
  dateLeft: DateOrISOString,
  dateRight: DateOrISOString,
): boolean => {
  const parsedDateLeft = parseDate(dateLeft);
  const parsedDateRight = parseDate(dateRight);
  return isSameYear(parsedDateLeft, parsedDateRight);
};

export const getDurationBetweenDates = (
  startDate: DateOrISOString,
  endDate: DateOrISOString,
): Duration => {
  const parsedStartDate = parseDate(startDate);
  const parsedEndDate = parseDate(endDate);

  return intervalToDuration({
    start: parsedStartDate,
    end: parsedEndDate,
  });
};

/**
 * Formats a date and time into ISO format without milliseconds
 * @param date - The date to format
 * @param time - The time string in HH:mm:ss format
 * @returns Formatted ISO string without milliseconds
 */
export const formatEventDateTime = (
  date: DateOrISOString,
  time: string,
): string => {
  // Parse the date
  const dateObj = parseDate(date);

  // Extract time components
  const [hours, minutes, seconds] = time.split(':').map(Number);

  // Set the time components
  dateObj.setHours(hours, minutes, seconds);

  // Format to ISO string and remove milliseconds
  return dateObj.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

export const getDaysBetweenDates = (
  startDate: DateOrISOString,
  endDate: DateOrISOString,
): Date[] => {
  const parsedStartDate = parseDate(startDate);
  const parsedEndDate = parseDate(endDate);
  return eachDayOfInterval({ start: parsedStartDate, end: parsedEndDate });
};

/**
 * Converts a date string to a normalized date string Eg 2024/1/2 => 2024-01-02
 * @param date - The date string to convert
 * @param format - The format of the date string
 * @returns The normalized date string
 */
export const normalizeDateString = (
  date: string,
  format: string = DATE_FORMAT.STANDARD_DATE,
): string => {
  const parsedDate = parse(date, format, new Date());
  const formattedDate = formatDate(parsedDate, format);
  return formattedDate;
};

/**
 * Calculates the number of business days (excluding weekends) between two dates
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns Number of business days
 */
export const getNumberOfBusinessDays = (
  startDate: Date,
  endDate: Date,
): {
  businessDays: number;
  weekendDays: number;
  totalDays: number;
  isWeekendOnly: boolean;
  isBusinessDayOnly: boolean;
} => {
  const parsedDateLeft = parseDate(startDate);
  const parsedDateRight = parseDate(endDate);
  const days = eachDayOfInterval({
    start: parsedDateLeft,
    end: parsedDateRight,
  });

  let businessDays = 0;
  let weekendDays = 0;

  for (const day of days) {
    const dayOfWeek = getDay(day);
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      weekendDays++;
    } else {
      businessDays++;
    }
  }

  return {
    businessDays,
    weekendDays,
    totalDays: days.length,
    isWeekendOnly: businessDays === 0 && weekendDays > 0,
    isBusinessDayOnly: businessDays > 0 && weekendDays === 0,
  };
};

/**
 * Converts a start date to the beginning of the day (00:00:00.000)
 * @param startDate - The start date string or Date object
 * @returns ISO string with time set to 00:00:00.000
 */
export const convertToStartOfDay = (startDate: DateOrISOString): string => {
  const date = parseDate(startDate);
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

/**
 * Converts an end date to the end of the day (23:59:59.999)
 * @param endDate - The end date string or Date object
 * @returns ISO string with time set to 23:59:59.999
 */
export const convertToEndOfDay = (endDate: DateOrISOString): string => {
  const date = parseDate(endDate);
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
};

/**
 * Combines a given date with a time string ("HH:mm:ss").
 *
 * @param date - A Date object representing the date (e.g., 2025-08-19).
 * @param timeString - A time string in "HH:mm:ss" format (e.g., "07:45:00").
 * @returns A new Date object that has the same date as `date` but the time set from `timeString`.
 */
export const combineDateWithTime = (date: Date, timeString: string): Date => {
  const timeDate = parseWithFormat(timeString, 'HH:mm:ss', new Date());

  let combinedDate = new Date(date);
  combinedDate = setTimeUnit(
    combinedDate,
    TimeUnit.HOURS,
    getTimeUnit(timeDate, TimeUnit.HOURS),
  );
  combinedDate = setTimeUnit(
    combinedDate,
    TimeUnit.MINUTES,
    getTimeUnit(timeDate, TimeUnit.MINUTES),
  );
  combinedDate = setTimeUnit(
    combinedDate,
    TimeUnit.SECONDS,
    getTimeUnit(timeDate, TimeUnit.SECONDS),
  );

  return combinedDate;
};
